// Helpers shared by the Time, pay & finance pages (Timesheets, Invoices, Financials, Billing, Mileage).
//
// Presentation only: labels, table classes, chart colours, notices. Nothing here calculates or rounds
// an amount — money is written with the app-wide `money()` (utils/format.js), and every figure comes
// from the server exactly as it was sent.
import React from 'react';
import clsx from 'clsx';
import { AlertTriangle, CheckCircle2, Info, RefreshCw, X } from 'lucide-react';
import { money, num, compact, fmtDate, fmtDayMonth } from '../../components/ui';
import { codeLabel } from '../../utils/format';

// ─── Labels ──────────────────────────────────────────────────────────────────

// Stored codes to words ('community_access' → 'Community access'). Lives in utils/format.js so
// pages outside finance (reports, client records) use the same rule; re-exported here for the
// finance pages that already import it from this kit.
export { codeLabel };

// '9.5% margin' / '9.5% loss' — the sign is in the word, never a double negative ('-9.5% loss').
export const marginLabel = (profit, revenue) => {
  const r = Number(revenue) || 0;
  if (!r) return 'No revenue in range';
  const m = ((Number(profit) || 0) / r) * 100;
  return `${num(Math.abs(m), 1)}% ${m < 0 ? 'loss' : 'margin'}`;
};

// Whole cents, for comparing two money figures without floating-point noise. Comparison only.
export const cents = (n) => Math.round((Number(n) || 0) * 100);

// Period keys from the grouped reports: 'YYYY-MM-DD' (a day, or the Monday a week starts) and
// 'YYYY-MM' (a month).
export const periodLabel = (p, group) => {
  if (!p) return '—';
  const s = String(p);
  if (/^\d{4}-\d{2}$/.test(s)) {
    const [y, m] = s.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
  }
  if (group === 'week') return `Week of ${fmtDayMonth(s)}`;
  return fmtDate(s);
};

// ─── Tables ──────────────────────────────────────────────────────────────────
// Hand-written tables share one look: white header, sentence-case labels, numbers right-aligned in
// tabular figures, and every table inside its own horizontal scroll area.
export const TABLE_WRAP = 'overflow-x-auto';
export const TABLE = 'w-full text-sm border-collapse';
export const THEAD_ROW = 'border-b border-slate-200 bg-white';
export const TH = 'px-4 py-2.5 text-left text-[12px] font-semibold text-slate-500 whitespace-nowrap';
export const TH_NUM = 'px-4 py-2.5 text-right text-[12px] font-semibold text-slate-500 whitespace-nowrap';
export const TR = 'border-b border-slate-100 last:border-0 hover:bg-slate-50/60';
export const TD = 'px-4 py-2.5 text-slate-700 align-middle';
export const TD_NUM = 'px-4 py-2.5 text-right tabular-nums text-slate-900 whitespace-nowrap align-middle';
export const TFOOT_ROW = 'border-t border-slate-300 bg-slate-50/70 font-semibold';

// A money cell: slate by default, red only when the amount is negative. A loss can never look like
// a win, and a cost is not shown in an error colour just for being a cost.
export function MoneyCell({ value, className, strong }) {
  const n = Number(value) || 0;
  return (
    <span className={clsx('tabular-nums whitespace-nowrap', strong && 'font-semibold', n < 0 ? 'text-red-600' : 'text-slate-900', className)}>
      {money(n)}
    </span>
  );
}

// ─── Notices ─────────────────────────────────────────────────────────────────

const NOTICE_TONE = {
  amber: { wrap: 'border-amber-200 bg-amber-50', icon: 'text-amber-600', title: 'text-amber-900', body: 'text-amber-800' },
  rose:  { wrap: 'border-rose-200 bg-rose-50',   icon: 'text-rose-600',  title: 'text-rose-900',  body: 'text-rose-800' },
  slate: { wrap: 'border-slate-200 bg-slate-50', icon: 'text-slate-500', title: 'text-slate-800', body: 'text-slate-600' },
  green: { wrap: 'border-emerald-200 bg-emerald-50', icon: 'text-emerald-600', title: 'text-emerald-900', body: 'text-emerald-800' },
};

// A notice that stays until it is dealt with or dismissed — for problems that are money at risk
// (a shift approved but not billed) and must not vanish with a four-second toast.
export function Notice({ tone = 'amber', title, children, actions, onDismiss, className, role = 'status' }) {
  const t = NOTICE_TONE[tone] || NOTICE_TONE.amber;
  const Icon = tone === 'slate' ? Info : tone === 'green' ? CheckCircle2 : AlertTriangle;
  return (
    <div role={role} className={clsx('flex items-start gap-3 rounded-xl border px-4 py-3', t.wrap, className)}>
      <Icon size={16} className={clsx('mt-0.5 shrink-0', t.icon)} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        {title && <p className={clsx('text-sm font-semibold', t.title)}>{title}</p>}
        {children && <div className={clsx('text-[13px] leading-relaxed', title && 'mt-0.5', t.body)}>{children}</div>}
        {actions && <div className="mt-2 flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {onDismiss && (
        <button type="button" onClick={onDismiss} aria-label="Dismiss"
          className="-mr-1 -mt-0.5 shrink-0 rounded-lg p-1.5 text-slate-500 hover:bg-white/70 hover:text-slate-700">
          <X size={15} />
        </button>
      )}
    </div>
  );
}

// When a table's totals row does not add up to the summary above it, say so and by how much,
// instead of quietly showing two different numbers. Renders nothing when everything agrees.
export function TotalsCheck({ checks = [] }) {
  const off = checks.filter((c) => c && cents(c.computed) !== cents(c.expected));
  if (!off.length) return null;
  return (
    <Notice tone="amber" title="Totals differ from the summary">
      {off.map((c) => (
        <span key={c.label} className="block">
          {c.label}: table {money(c.computed)}, summary {money(c.expected)} (difference {money(Number(c.computed) - Number(c.expected))}).
        </span>
      ))}
    </Notice>
  );
}

// ─── Controls ────────────────────────────────────────────────────────────────

// A labelled From / To pair for report headers.
export function DateRange({ from, to, onFrom, onTo, idPrefix = 'range', maxTo }) {
  return (
    <div className="flex flex-wrap items-end gap-2">
      <div>
        <label htmlFor={`${idPrefix}-from`} className="mb-1 block text-[12px] font-medium text-slate-500">From</label>
        <input id={`${idPrefix}-from`} type="date" value={from || ''} max={to || undefined}
          onChange={(e) => onFrom(e.target.value)} className="input-field !w-auto !py-2 text-sm" />
      </div>
      <div>
        <label htmlFor={`${idPrefix}-to`} className="mb-1 block text-[12px] font-medium text-slate-500">To</label>
        <input id={`${idPrefix}-to`} type="date" value={to || ''} min={from || undefined} max={maxTo || undefined}
          onChange={(e) => onTo(e.target.value)} className="input-field !w-auto !py-2 text-sm" />
      </div>
    </div>
  );
}

// Toggle buttons on a slate track (date presets, grouping). The active one is white and
// aria-pressed, so the choice is visible and announced.
export function Segmented({ options = [], value, onChange, ariaLabel, size = 'md', className }) {
  return (
    <div role="group" aria-label={ariaLabel} className={clsx('inline-flex flex-wrap items-center gap-0.5 rounded-lg bg-slate-100 p-0.5', className)}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button key={o.value} type="button" aria-pressed={on} onClick={() => onChange(o.value)}
            className={clsx('inline-flex items-center gap-1.5 rounded-md px-3 font-medium whitespace-nowrap transition-colors duration-150',
              size === 'sm' ? 'min-h-[30px] text-[12px]' : 'min-h-[36px] text-[13px]',
              on ? 'bg-white text-slate-900 shadow-[0_1px_2px_rgba(16,24,40,.08)]' : 'text-slate-600 hover:text-slate-900')}>
            {o.label}
            {o.count != null && <span className={clsx('tabular-nums text-[11px] font-semibold', on ? 'text-slate-500' : 'text-slate-500')}>{o.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

// Icon-only refresh with a real label.
export function RefreshButton({ onClick, loading, label = 'Refresh', className }) {
  return (
    <button type="button" onClick={onClick} disabled={loading} aria-label={label} title={label}
      className={clsx('inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50', className)}>
      <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
    </button>
  );
}

// ─── Charts ──────────────────────────────────────────────────────────────────
// One muted palette: revenue dark slate, costs in lighter neutrals, profit the primary accent, and
// red only for a loss.
export const CHART = {
  revenue: '#334155',   // slate-700
  staff: '#94a3b8',     // slate-400
  super: '#b6c2d1',
  fuel: '#cbd5e1',      // slate-300
  expenses: '#64748b',  // slate-500
  profit: '#4f46e5',    // primary-600
  loss: '#dc2626',      // red-600
  grid: '#f1f5f9',
  axis: '#64748b',
};

// Axis ticks are scale marks, not amounts, so they may be abbreviated ('$7k'). Nothing else is.
export const axisMoney = (v) => {
  const n = Number(v) || 0;
  return `${n < 0 ? '-' : ''}$${compact(Math.abs(n))}`;
};

// Chart tooltip: every value to the cent, negative in red.
export function MoneyTooltip({ active, payload, label, labelFormatter }) {
  if (!active || !payload || !payload.length) return null;
  const heading = label == null || label === '' ? null : (labelFormatter ? labelFormatter(label) : label);
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-card-deep">
      {heading && <p className="mb-1.5 font-semibold text-slate-700">{heading}</p>}
      {payload.map((p, i) => (
        <p key={`${p.dataKey || ''}-${p.name || i}`} className="flex items-center justify-between gap-6">
          <span className="flex items-center gap-1.5 text-slate-600">
            <span className="h-2 w-2 rounded-sm" style={{ background: p.color || (p.payload && p.payload.fill) }} aria-hidden="true" />{p.name}
          </span>
          <span className={clsx('font-semibold tabular-nums', Number(p.value) < 0 ? 'text-red-600' : 'text-slate-900')}>{money(p.value)}</span>
        </p>
      ))}
    </div>
  );
}
